import { commandActions, commandPages } from "./command-meta";
import { settingsSections } from "@/mocks/settings";
import { ROLE_LABELS, type Role } from "./auth";
import type { PaletteGroup, PaletteItem } from "./command-palette";
import { api } from "@/lib/api";
import { isEn, t } from "@/lib/i18n";

/* Выдача командной палитры: страницы — из списка разделов плюс разделы настроек, команды — подсказки.
   Контакты, люди, команды и замечания ищутся на сервере (/api/search); беседы и рассылки добавятся с этапами 3 и 7. */

export function buildCommandGroups(): PaletteGroup[] {
  const conversations: PaletteItem[] = [
    { id: "conv-by-name", label: t("Поиск бесед по имени"), icon: "ri-search-line", hint: t("беседа"), keywords: t("диалог клиент чат поиск имя") },
    { id: "conv-by-area", label: t("Фильтрация разговоров по областям"), icon: "ri-filter-3-line", hint: t("беседа"), keywords: t("фильтр диалог область канал этап") },
  ];

  const pages: PaletteItem[] = [
    ...commandPages.map((p) => ({ id: p.id, label: p.label, icon: p.icon, keywords: p.keywords, anchor: p.anchor })),
    ...settingsSections.map((s) => ({
      id: `settings-${s.id}`,
      label: s.label,
      icon: s.icon,
      hint: t("настройки"),
      keywords: t("{text} настройки рабочего пространства", { text: s.text }),
      tab: s.id,
    })),
  ];

  const actions: PaletteItem[] = commandActions.map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    hint: isEn ? "command" : "команда",
    keywords: a.keywords,
  }));

  return [
    { id: "conversations", label: t("Беседы"), items: conversations },
    { id: "pages", label: t("Страницы"), items: pages },
    { id: "actions", label: isEn ? "Commands" : "Команды", items: actions },
  ];
}

type SearchResult = {
  users: { id: string; name: string; email: string; role: Role }[];
  teams: { id: string; name: string; description: string }[];
  feedback: { id: number; kind: string; status: string; title: string }[];
  contacts: { id: string; name: string; phone: string | null; email: string | null; company: string }[];
};

/** Поиск на сервере: контакты, люди, команды, замечания (агент видит только свои контакты и замечания) */
export async function searchRemote(q: string): Promise<PaletteGroup[]> {
  const r = await api<SearchResult>(`/search?q=${encodeURIComponent(q)}`);
  const groups: PaletteGroup[] = [
    {
      id: "contacts",
      label: t("Контакты"),
      items: (r.contacts ?? []).map((c) => ({
        id: `contact-${c.id}`,
        label: c.name,
        icon: "ri-contacts-book-2-line",
        hint: [c.company, c.phone || c.email].filter(Boolean).join(" · ") || t("контакт"),
        anchor: "#contacts-app",
      })),
    },
    {
      id: "people",
      label: t("Люди"),
      items: r.users.map((u) => ({ id: `user-${u.id}`, label: u.name, icon: "ri-user-line", hint: `${ROLE_LABELS[u.role] ?? u.role} · ${u.email}`, anchor: "#user-settings" })),
    },
    {
      id: "teams",
      label: t("Команды"),
      items: r.teams.map((tm) => ({ id: `team-${tm.id}`, label: tm.name, icon: "ri-team-line", hint: tm.description || t("команда"), anchor: "#team-settings" })),
    },
    {
      id: "feedback",
      label: t("Замечания"),
      items: r.feedback.map((f) => ({
        id: `fb-${f.id}`,
        label: t("№{id} {title}", { id: f.id, title: f.title }),
        icon: f.kind === "idea" ? "ri-lightbulb-line" : "ri-error-warning-line",
        hint: f.kind === "idea" ? t("идея") : t("проблема"),
        tab: "feedback",
      })),
    },
  ];
  return groups.filter((g) => g.items.length > 0);
}
